import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTimes, 
    faCheckCircle, 
    faTimesCircle, 
    faExclamationCircle,
    faFileInvoice,
    faDollarSign,
    faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { getErrorMessage } from '../../utils/errorMessages';
import './ConversionApprovalModal.css';

const ConversionApprovalModal = ({ isOpen, onClose, invoice, isFinancialApprover, onApprovalAction }) => {
    const [selectedAction, setSelectedAction] = useState(null); // 'approve' or 'reject'
    const [rejectionReason, setRejectionReason] = useState('');
    const [notes, setNotes] = useState('');
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isOpen || !invoice) return null;

    const handleActionSelect = (action) => {
        setSelectedAction(action);
        setError('');
        // Clear rejection reason if not rejecting
        if (action !== 'reject') {
            setRejectionReason('');
        }
    };

    const handleSubmit = async () => {
        // Validation
        if (!selectedAction) {
            setError('Please select an action');
            return;
        }

        if (selectedAction === 'reject' && !rejectionReason.trim()) {
            setError('Rejection reason is required when rejecting a conversion request');
            return;
        }

        setIsSubmitting(true);
        setError('');

        try {
            await onApprovalAction({
                action: selectedAction,
                rejection_reason: rejectionReason,
                notes: notes
            });

            // Reset form
            setSelectedAction(null);
            setRejectionReason('');
            setNotes('');
            onClose();
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to process conversion approval. Please try again.'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget && !isSubmitting) {
            onClose();
        }
    };

    const formatCurrency = (amount, currency = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatStatus = (status) => {
        if (!status) return 'N/A';
        // Convert underscore-separated status to Title Case
        return status
            .replace(/_/g, ' ')
            .replace(/\b\w/g, char => char.toUpperCase());
    };

    return (
        <div className="conversion-approval-modal-overlay" onClick={handleOverlayClick}>
            <div className="conversion-approval-modal">
                {/* Header */}
                <div className="conversion-approval-modal-header">
                    <div className="conversion-approval-modal-header-content">
                        <h3>
                            <FontAwesomeIcon icon={faArrowRight} /> Conversion Approval Required
                        </h3>
                        <div className="conversion-approval-invoice-number">
                            Pro Forma: {invoice.invoice_number}
                        </div>
                    </div>
                    <button 
                        className="conversion-approval-close-button" 
                        onClick={onClose}
                        disabled={isSubmitting}
                        type="button"
                    >
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                </div>

                {/* Body */}
                <div className="conversion-approval-modal-body">
                    {/* Invoice Summary */}
                    <div className="conversion-approval-invoice-summary">
                        <h4>Pro Forma Invoice Details</h4>
                        <div className="conversion-approval-detail-grid">
                            <div className="conversion-approval-detail-item">
                                <span className="conversion-approval-detail-label">Partner:</span>
                                <span className="conversion-approval-detail-value">{invoice.partner_name}</span>
                            </div>
                            <div className="conversion-approval-detail-item">
                                <span className="conversion-approval-detail-label">Invoice Date:</span>
                                <span className="conversion-approval-detail-value">{formatDate(invoice.invoice_date)}</span>
                            </div>
                            <div className="conversion-approval-detail-item">
                                <span className="conversion-approval-detail-label">Due Date:</span>
                                <span className="conversion-approval-detail-value">{formatDate(invoice.due_date)}</span>
                            </div>
                            <div className="conversion-approval-detail-item">
                                <span className="conversion-approval-detail-label">Entity:</span>
                                <span className="conversion-approval-detail-value">{invoice.hms_entity_name || 'N/A'}</span>
                            </div>
                            <div className="conversion-approval-detail-item conversion-approval-amount-highlight">
                                <span className="conversion-approval-detail-label">
                                    <FontAwesomeIcon icon={faDollarSign} /> Total Amount:
                                </span>
                                <span className="conversion-approval-detail-value">
                                    {formatCurrency(invoice.gross_amount, invoice.currency)}
                                </span>
                            </div>
                            <div className="conversion-approval-detail-item">
                                <span className="conversion-approval-detail-label">Status:</span>
                                <span className={`conversion-approval-status-badge ${invoice.status?.toLowerCase()}`}>
                                    {formatStatus(invoice.status)}
                                </span>
                            </div>
                        </div>
                    </div>
                    
                    {/* Info Banner */}
                    <div className="conversion-info-banner">
                        <FontAwesomeIcon icon={faExclamationCircle} />
                        <div>
                            <strong>Conversion Request</strong>
                            <p>Upon approval, this pro forma will be automatically converted to an outgoing invoice with a new invoice number.</p>
                        </div>
                    </div>

                    {/* Check if user is Financial Approver */}
                    {!isFinancialApprover ? (
                        <div className="conversion-approval-error-message">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <span>You do not have permission to approve conversions. Only Financial Approvers can perform this action.</span>
                        </div>
                    ) : invoice.status !== 'PENDING_CONVERSION_APPROVAL' ? (
                        <div className="conversion-approval-error-message">
                            <FontAwesomeIcon icon={faExclamationCircle} />
                            <span>This invoice is not pending conversion approval. Current status: {formatStatus(invoice.status)}</span>
                        </div>
                    ) : (
                        <>
                            {/* Action Buttons */}
                            <div className="conversion-approval-actions-section">
                                <h4>Select Action</h4>
                                <div className="conversion-approval-action-buttons">
                                    <button 
                                        className={`conversion-approval-action-button approve ${selectedAction === 'approve' ? 'active' : ''}`}
                                        onClick={() => handleActionSelect('approve')}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faCheckCircle} />
                                        Approve Conversion
                                    </button>
                                    <button 
                                        className={`conversion-approval-action-button reject ${selectedAction === 'reject' ? 'active' : ''}`}
                                        onClick={() => handleActionSelect('reject')}
                                        type="button"
                                    >
                                        <FontAwesomeIcon icon={faTimesCircle} />
                                        Reject Conversion
                                    </button>
                                </div>
                            </div>

                            {/* Rejection Reason (conditionally shown) */}
                            {selectedAction === 'reject' && (
                                <div className="conversion-approval-form-section">
                                    <label>
                                        Rejection Reason <span className="required">*</span>
                                    </label>
                                    <textarea
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        placeholder="Please provide a detailed reason for rejecting this conversion request..."
                                        disabled={isSubmitting}
                                    />
                                </div>
                            )}

                            {/* Optional Notes - Only for Approve */}
                            {selectedAction === 'approve' && (
                                <div className="conversion-approval-form-section">
                                    <label>Approval Notes (Optional)</label>
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Add any additional notes or comments about this conversion approval..."
                                        disabled={isSubmitting}
                                    />
                                </div>
                            )}

                            {/* Error Message */}
                            {error && (
                                <div className="conversion-approval-error-message">
                                    <FontAwesomeIcon icon={faExclamationCircle} />
                                    <span>{error}</span>
                                </div>
                            )}

                            {/* Submit Button */}
                            {selectedAction && (
                                <button 
                                    className="conversion-approval-submit-button"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                    type="button"
                                >
                                    {isSubmitting ? 'Processing...' : `${selectedAction === 'approve' ? 'Approve' : 'Reject'} Conversion`}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConversionApprovalModal;

